// TagManagementPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/strategyxs.png'

const TagManagementPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const tagManagementStats = [
        { number: '78%', label: 'improvement in data accuracy with proper tag management' },
        { number: '60%', label: 'faster tag deployment with centralized management systems' },
        { number: '45%', label: 'reduction in site loading times with optimized tagging' },
        { number: '3.2x', label: 'higher confidence in campaign performance data' }
    ];

    const tagManagementApproaches = [
        {
            icon: 'fas fa-road',
            title: 'Tag Management Strategy & Planning',
            desc: 'Comprehensive data collection planning, tag taxonomy and naming conventions, triggering logic and event definitions, and governance framework design.'
        },
        {
            icon: 'fas fa-box',
            title: 'Implementation & Centralized Tag Setup',
            desc: 'Tag container configuration and deployment (GTM, Tealium, etc.), migration of existing tags, analytics and advertising tag installation, and server-side/client-side tagging solutions.'
        },
        {
            icon: 'fas fa-headset',
            title: 'Expert Consultancy & Support',
            desc: 'Technical review of existing tracking setups, tag optimization and error resolution, GDPR-aligned configuration, and best practices guidance for compliance and efficiency.'
        },
        {
            icon: 'fas fa-chart-bar',
            title: 'Reporting & Insight Integration',
            desc: 'Tag performance reporting, event tracking validation, data quality checks, and seamless integration with analytics dashboards and BI tools for actionable insights.'
        },
        {
            icon: 'fas fa-shield-alt',
            title: 'Ongoing Monitoring & Tag Governance',
            desc: 'Regular audits for duplicate or broken tags, maintenance of central tag libraries, version documentation, change logs, and automated testing workflows.'
        },
        {
            icon: 'fas fa-sync-alt',
            title: 'Performance Optimization & Maintenance',
            desc: 'Continuous tag performance monitoring, load time optimization, tag sequencing improvements, and proactive maintenance for optimal data collection and site performance.'
        }
    ];

    const benefits = [
        ['Unified Data Collection', 'Accurate, consistent metrics across all channels and platforms for reliable insights'],
        ['Faster Deployment & Agility', 'Reduced reliance on developers with streamlined tag deployment and management processes'],
        ['Enhanced Campaign Measurement', 'Clear ROI tracking and performance insights for better optimization decisions'],
        ['Scalable Tracking Infrastructure', 'Future-ready setup that supports business growth and new technology integrations'],
        ['Improved Site Performance', 'Optimized tag loading for faster page speeds and better user experience']
    ];

    const targetBusinesses = [
        'Organizations with multiple tracking tools needing centralized management',
        'Marketing teams requiring accurate campaign measurement and attribution',
        'E-commerce businesses needing reliable conversion tracking and analytics',
        'Enterprises with complex digital ecosystems and multiple data sources',
        'Growth-focused companies scaling their analytics and measurement capabilities',
        'Businesses seeking GDPR-compliant data collection and tracking solutions'
    ];

    const tagManagementQuestions = [
        'Are you confident in the accuracy of your current tracking data?',
        'How much time do you spend managing and troubleshooting tracking tags?',
        'Is your site performance affected by multiple tracking scripts?',
        'Do you have a clear view of user behavior across all touchpoints?',
        'How quickly can you deploy new tracking requirements without developer help?'
    ];

    return (
        <div className="strategy sv-page">
            {/* Decorative Image - Top of Page */}
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Tag Management</span> Services
                    </h1>
                    <p>Centralized control, accurate data, and optimized performance for smarter insights</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    Accurate, dependable tracking is essential for understanding user behaviour, optimising campaigns, and driving business growth.
                                </p>
                                <p className="sv-description">
                                    Reachtix's <strong>Tag Management services</strong> ensure your digital ecosystem captures the right data — consistently, cleanly, and with minimal friction — so you can make informed decisions and power better outcomes across every channel.
                                </p>
                                <p className="sv-description">
                                    As your digital presence grows, so does the number of tracking scripts and code snippets. Without centralized management, this leads to inaccurate data, slower performance, and fragmented insights. Our tag management solutions provide the control and clarity you need to trust your data and act on it confidently.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-tags mr-2"></i>
                                        Start Tag Management Strategy
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Get Tag Management Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">Tag Management Matters</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {tagManagementStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What Is Tag Management Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is Tag Management?</h2>
                                <p className="sv-section-description">
                                    Tag management is the centralized control and deployment of all tracking scripts, pixels, and code snippets on your website or app. It enables businesses to manage their analytics, advertising, and marketing tags without constant developer intervention.
                                </p>
                                <p className="sv-section-description">
                                    In today's complex digital landscape with multiple tools and platforms, tag management ensures data accuracy, improves site performance, and provides the agility needed for fast-paced marketing optimization.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Tag Management Questions We Address:</h4>
                                    {tagManagementQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Tag Management Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Tag Management Drives Digital Success</h2>
                                <p className="sv-section-description">
                                    Tag management transforms chaotic tracking into organized intelligence, enabling organizations to make data-driven decisions with confidence and agility.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-database"></i>
                                        <h5>Data Accuracy & Consistency</h5>
                                        <p>Ensure reliable data collection across all platforms and touchpoints</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bolt"></i>
                                        <h5>Operational Agility</h5>
                                        <p>Deploy and update tracking without developer dependencies</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-tachometer-alt"></i>
                                        <h5>Performance Optimization</h5>
                                        <p>Improve site speed by optimizing tag loading and sequencing</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-shield-alt"></i>
                                        <h5>Compliance & Governance</h5>
                                        <p>Maintain GDPR compliance and organized tag documentation</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Tag Management Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Tag Management Capabilities at Reachtix</h2>
                                <p className="sv-section-description">
                                    We provide end-to-end tag management solutions — from initial strategy to ongoing governance — designed to support both marketing objectives and technical excellence.
                                </p>

                                <div className="sv-approaches-grid">
                                    {tagManagementApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Benefits of Tag Management with Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Benefits Most from Tag Management?</h2>
                                <p className="sv-section-description">
                                    Tag management delivers maximum value for organizations seeking reliable data and agile tracking capabilities:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Whether you're running complex e-commerce operations or scaling B2B marketing efforts, robust tag management capabilities unlock accurate insights that transform performance and drive growth.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Take Control of Your Digital Tracking?</h2>
                                <p className="sv-cta-description">
                                    Unlock accurate data and make smarter decisions with Reachtix's <strong>Tag Management services</strong>. Transform your tracking from a technical burden into a strategic advantage.
                                </p>
                                <p className="sv-cta-description">
                                    At Reachtix, we treat tag management as a strategic asset — not just a technical task. Our specialists blend technical precision with business insight to make your tracking setup a powerful driver of clarity and growth.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        className="sv-cta-primary-btn"
                                    >
                                        <i className="fas fa-tags mr-2"></i>
                                        Start Tag Management Journey
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        className="sv-cta-secondary-btn"
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Get Free Tag Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default TagManagementPage;